// Sesión: quién entró y la configuración pública (modo de acceso, IA, PDF, CRM de Carlos). Sale de GET /api/yo.
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { POLITICA_ACCESO_POR_OMISION, puede, puedeAsignarVendedor, puedeEnCotizacion } from '@cotizador/compartido';
import type { Accion, ConfigPublica, PoliticaAcceso, UsuarioSesion } from '@cotizador/compartido';
import { api } from './api/consultas.ts';
import { EVENTO_SIN_SESION, ErrorDeApi, mensajeDe } from './api/cliente.ts';
import { borrarTodosLosBorradores } from './logica/borrador-local.ts';
import { olvidarPendientes } from './logica/pendientes.ts';

export type EstadoSesion =
  | { tipo: 'cargando' }
  | { tipo: 'fuera' }
  | { tipo: 'error'; mensaje: string }
  | { tipo: 'dentro'; usuario: UsuarioSesion; config: ConfigPublica };

interface ValorSesion {
  estado: EstadoSesion;
  recargar: () => Promise<void>;
  salir: () => Promise<void>;
}

const Contexto = createContext<ValorSesion | null>(null);

export function ProveedorSesion({ children }: { children: ReactNode }) {
  const [estado, setEstado] = useState<EstadoSesion>({ tipo: 'cargando' });

  const recargar = useCallback(async () => {
    try {
      const r = await api.yo();
      setEstado({ tipo: 'dentro', usuario: r.usuario, config: r.config });
    } catch (e) {
      if (e instanceof ErrorDeApi && (e.codigo === 'NO_AUTENTICADO' || e.estado === 401)) setEstado({ tipo: 'fuera' });
      else setEstado({ tipo: 'error', mensaje: mensajeDe(e) });
    }
  }, []);

  const salir = useCallback(async () => {
    // Hallazgo del 10-oct: el cuestionario sin enviar (nombre, teléfono, notas del cliente) no se queda en el navegador
    // para el siguiente que entre.
    borrarTodosLosBorradores();
    // lo tecleado y sin guardar en «Ampliación» es de esta sesión: al salir no se queda en memoria para el que entre después
    olvidarPendientes();
    try {
      await api.salir();
    } finally {
      setEstado({ tipo: 'fuera' });
    }
  }, []);

  useEffect(() => {
    void recargar();
    const fuera = () => setEstado({ tipo: 'fuera' });
    window.addEventListener(EVENTO_SIN_SESION, fuera);
    return () => window.removeEventListener(EVENTO_SIN_SESION, fuera);
  }, [recargar]);

  return <Contexto.Provider value={{ estado, recargar, salir }}>{children}</Contexto.Provider>;
}

export function usarSesion(): ValorSesion {
  const v = useContext(Contexto);
  if (!v) throw new Error('usarSesion fuera de <ProveedorSesion>');
  return v;
}

export interface ValorUsuario {
  usuario: UsuarioSesion;
  config: ConfigPublica;
  /** Política de acceso vigente (la publica el servidor; un servidor de antes del contrato v2 no la manda = apagada). */
  politica: PoliticaAcceso;
  /** ¿Su rol puede hacer esto? (sin mirar ninguna cotización: crear, configurar…). */
  puede: (a: Accion) => boolean;
  /** ¿Puede hacer esto en ESTA cotización? Rol + política "vendedor externo edita solo lo suyo". Úsala en todo lo que
   *  edita o captura dentro de una cotización (el servidor responde 403 si la interfaz se equivoca).
   *  PAPELERA (verificación adversarial del 13-oct): una cotización en la papelera no la edita nadie (ni Diego sin
   *  recuperarla): aquí da false para TODO, así cada sección queda de solo lectura sin preguntar por su cuenta. */
  puedeEn: (a: Accion, cotizacion: { vendedor_id: string; papelera?: unknown }) => boolean;
  /** ¿Puede dejar una cotización a nombre de este vendedor (al crearla o al cambiarle el vendedor)? */
  puedeAsignar: (vendedorId: string) => boolean;
  /** ADENDA A0b (11-oct): solo el director ve costos, margen y utilidad (el servidor no se los manda a nadie más). */
  veCostos: boolean;
  /** Semáforo de margen (Diego, 11-oct): el director y los asesores internos ven el color; los externos no. */
  veSemaforo: boolean;
}

/** Para pantallas que solo existen con sesión: truena si se usa fuera (fallo ruidoso). */
export function usarUsuario(): ValorUsuario {
  const { estado } = usarSesion();
  if (estado.tipo !== 'dentro') throw new Error('usarUsuario sin sesión');
  // UsuarioSesion no trae "activo": si el servidor la dio, está activo.
  const yo = { id: estado.usuario.id, rol: estado.usuario.rol, activo: true };
  const politica = estado.config.politica_acceso ?? POLITICA_ACCESO_POR_OMISION;
  return {
    usuario: estado.usuario,
    config: estado.config,
    politica,
    puede: (a) => puede(yo, a),
    puedeEn: (a, cotizacion) => !cotizacion.papelera && puedeEnCotizacion(yo, a, cotizacion, politica),
    puedeAsignar: (vendedorId) => puedeAsignarVendedor(yo, vendedorId, politica),
    veCostos: puede(yo, 'ver_costos'),
    veSemaforo: puede(yo, 'ver_semaforo'),
  };
}
